/*
 * Carbisdale - DictationPostProcessor
 *
 * Pure Functions für die Nachverarbeitung von Deepgram-Transkripten.
 * Wandelt gesprochene Diktatbefehle in Satzzeichen und Steuerzeichen um.
 *
 * Deepgrams smart_format=true erledigt bereits:
 * - Automatische Groß-/Kleinschreibung
 * - Basis-Interpunktion (Punkt am Satzende, Komma bei Pausen)
 * - Zahlenformatierung (Englisch)
 *
 * Dieses Modul ergänzt:
 * - Deutsche und englische Sprachbefehle ("Komma" → ",", "comma" → "," etc.)
 * - Absatz-/Zeilenumbruch-Erkennung ("Neue Zeile" / "new line", "Neuer Absatz" / "new paragraph")
 *
 * Beide Befehlssätze sind parallel aktiv — Deepgram (language=multi) erkennt
 * die Sprache selbst, der Nutzer diktiert einfach in seiner Sprache.
 */

// Diktatbefehle → Satzzeichen (Deutsch + Englisch)
// Sortiert nach Länge (longest first), damit "Ausrufezeichen" vor kürzeren matcht
const PUNCTUATION_COMMANDS = [
    { pattern: 'anführungszeichen', replacement: null, quoteStyle: 'de' },  // Toggle-Logik
    { pattern: 'exclamation point', replacement: '!' },
    { pattern: 'exclamation mark', replacement: '!' },
    { pattern: 'ausrufezeichen', replacement: '!' },
    { pattern: 'gedankenstrich', replacement: ' — ' },
    { pattern: 'quotation mark', replacement: null, quoteStyle: 'en' },     // Toggle-Logik
    { pattern: 'question mark', replacement: '?' },
    { pattern: 'fragezeichen', replacement: '?' },
    { pattern: 'doppelpunkt', replacement: ':' },
    { pattern: 'bindestrich', replacement: '-' },
    { pattern: 'semicolon', replacement: ';' },
    { pattern: 'semikolon', replacement: ';' },
    { pattern: 'full stop', replacement: '.' },
    { pattern: 'hyphen', replacement: '-' },
    { pattern: 'period', replacement: '.' },
    { pattern: 'comma', replacement: ',' },
    { pattern: 'colon', replacement: ':' },
    { pattern: 'punkt', replacement: '.' },
    { pattern: 'komma', replacement: ',' },
    { pattern: 'dash', replacement: ' — ' },
]

// Befehle für Absatz-/Zeilenumbruch (Deutsch + Englisch)
const BREAK_COMMANDS = [
    'neuer absatz',
    'new paragraph',
    'neue zeile',
    'new line',
]

// Anführungszeichen-Paare pro Befehls-Sprache (Deutsch: „...", Englisch: “...”)
const QUOTES = {
    de: { open: '„', close: '"' },
    en: { open: '“', close: '”' },
}

/**
 * Verarbeitet ein Deepgram-Transkript: Deutsche Diktatbefehle → Zeichen.
 *
 * @param {string} text - Erkannter Text von Deepgram
 * @param {object} [state] - Zustand für Toggle-Logik (wird mutiert)
 * @param {boolean} [state.quoteOpen] - Ob ein Anführungszeichen offen ist
 * @returns {{ text: string, breaks: number[] }}
 *   text: Verarbeiteter Text (ohne Umbruch-Befehle)
 *   breaks: Offsets im verarbeiteten Text, an denen Absatzumbrüche stehen sollen
 */
export function processDictationText(text, state = null) {
    if (!text || text.trim() === '') {
        return { text: '', breaks: [] }
    }

    // Zuerst Umbruch-Befehle extrahieren
    const breakResult = _extractBreakCommands(text)
    let processed = breakResult.text
    const breaks = breakResult.breaks

    // Dann Satzzeichen-Befehle ersetzen
    processed = _replacePunctuationCommands(processed, state)

    // Aufräumen: doppelte Leerzeichen entfernen
    processed = _cleanupSpaces(processed)

    return { text: processed, breaks }
}

/**
 * Erstellt einen frischen Post-Processor-State.
 * @returns {{ quoteOpen: boolean }}
 */
export function createPostProcessorState() {
    return { quoteOpen: false }
}


// === Interne Hilfsfunktionen ===

/**
 * Extrahiert Umbruch-Befehle aus dem Text und berechnet die Offsets
 * im resultierenden Text, an denen Absatzumbrüche eingefügt werden sollen.
 *
 * Zwischen den Teilen bleibt ein Leerzeichen erhalten (außer am Anfang/Ende),
 * damit Wörter nicht zusammenkleben.
 */
function _extractBreakCommands(text) {
    const breaks = []
    let result = text

    for (const cmd of BREAK_COMMANDS) {
        // Befehl mit optionaler Interpunktion+Whitespace davor UND danach matchen
        // (Deepgram setzt oft Punkte rund um "Neue Zeile" wegen smart_format)
        const regex = new RegExp(`[.,!?:;]*\\s*\\b${cmd}\\b[.,!?:;]*\\s*`, 'gi')
        let match
        while ((match = regex.exec(result)) !== null) {
            const before = result.substring(0, match.index)
            const after = result.substring(match.index + match[0].length)

            // Leerzeichen zwischen den Teilen erhalten (außer am Anfang/Ende)
            const separator = (before.length > 0 && after.length > 0) ? ' ' : ''
            const breakOffset = before.length > 0 ? before.length : 0

            breaks.push(breakOffset)
            result = before + separator + after

            // Regex zurücksetzen wegen geändertem String
            regex.lastIndex = before.length + separator.length
        }
    }

    // Breaks sortieren (aufsteigend)
    breaks.sort((a, b) => a - b)

    return { text: result, breaks }
}

/**
 * Ersetzt Satzzeichen-Befehle im Text.
 * Matching ist case-insensitive und nur an Wortgrenzen.
 */
function _replacePunctuationCommands(text, state) {
    let result = text

    for (const cmd of PUNCTUATION_COMMANDS) {
        const regex = new RegExp(`\\b${cmd.pattern}\\b`, 'gi')

        result = result.replace(regex, (match) => {
            // Anführungszeichen: Toggle-Logik
            if (cmd.replacement === null) {
                return _handleQuoteToggle(state, cmd.quoteStyle)
            }

            // Normales Satzzeichen: vorhergehendes Leerzeichen entfernen
            // wird in _cleanupSpaces behandelt
            return cmd.replacement
        })
    }

    // Satzzeichen direkt an vorheriges Wort kleben (kein Leerzeichen davor)
    result = result.replace(/\s+([.,!?:;\-])/g, '$1')

    // Bindestrich: auch nachfolgendes Leerzeichen entfernen (Hans-Peter, nicht Hans- Peter)
    result = result.replace(/(-)\s+/g, '$1')

    // Schließendes Anführungszeichen an vorheriges Wort kleben (nur " und ")
    result = result.replace(/\s+(["\u201D])/g, '$1')

    // Nach Satzzeichen (außer Bindestrich) Leerzeichen sicherstellen
    result = result.replace(/([.,!?:;])(?=[A-Za-zÀ-ÿ\u201E„“])/g, '$1 ')

    return result
}

/**
 * Toggle-Logik für Anführungszeichen.
 * @param {object|null} state - Post-Processor-State (quoteOpen)
 * @param {string} style - Schlüssel in QUOTES ('de' | 'en')
 */
function _handleQuoteToggle(state, style) {
    const pair = QUOTES[style]
    if (!state) {
        // Ohne State: immer öffnendes Anführungszeichen
        return pair.open
    }
    if (state.quoteOpen) {
        state.quoteOpen = false
        return pair.close
    } else {
        state.quoteOpen = true
        return pair.open
    }
}

/**
 * Bereinigt doppelte Leerzeichen und Whitespace-Artefakte.
 */
function _cleanupSpaces(text) {
    // Doppelte Leerzeichen → einfach
    let result = text.replace(/  +/g, ' ')
    // Führende/nachfolgende Leerzeichen trimmen
    result = result.trim()
    return result
}
